import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

interface ChatMessage {
  id: string;
  trip_id: string | null;
  user_id: string | null;
  content: string;
  created_at: string | null;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  } | null;
}

export default function TripChat() {
  const { user } = useAuth();
  const { tripId } = useParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const markMessagesAsRead = async (allMessages: ChatMessage[]) => {
    if (!user?.id || !tripId || allMessages.length === 0) return;

    // IDs aller Nachrichten extrahieren
    const messageIds = allMessages.map((msg) => msg.id);

    // Bestehende Read-Status abrufen
    const { data: existing, error: fetchError } = await supabase
      .from("chat_read_status")
      .select("message_id")
      .in("message_id", messageIds)
      .eq("user_id", user.id);

    if (fetchError) {
      console.error("Fehler beim Prüfen der Lesestatus:", fetchError.message);
      return;
    }

    const alreadyReadIds = new Set((existing || []).map((r) => r.message_id));

    // Neue Einträge erstellen für ungelesene Nachrichten
    const inserts = allMessages
      .filter((msg) => !alreadyReadIds.has(msg.id))
      .map((msg) => ({
        message_id: msg.id,
        user_id: user.id,
        read: true,
      }));

    if (inserts.length > 0) {
      const { error: insertError } = await supabase
        .from("chat_read_status")
        .insert(inserts);

      if (insertError) {
        console.error(
          "Fehler beim Eintragen des Lesestatus:",
          insertError.message
        );
      }
    }
  };

  // Nachrichten laden
  useEffect(() => {
    if (!tripId) return;

    const loadMessages = async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*, profiles(first_name, last_name)")
        .eq("trip_id", tripId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Fehler beim Laden:", error.message);
      } else {
        setMessages(data);
        //scrollToEnd();
        await markMessagesAsRead(data); // ✅ neu
      }
    };

    loadMessages();

    // ✅ Realtime: neue Nachricht empfangen
    const channel = supabase
      .channel(`chat-${tripId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `trip_id=eq.${tripId}`,
        },
        async (payload) => {
          const newMsg = payload.new as ChatMessage;

          // ❌ EIGENE Nachricht ignorieren – sie wurde bereits hinzugefügt
          if (newMsg.user_id === user?.id) return;

          setMessages((prev) => [...prev, newMsg]);
          scrollToEnd();

          if (user?.id) {
            await supabase.from("chat_read_status").insert({
              message_id: newMsg.id,
              user_id: user.id,
              read: true,
            });
          }
        }
      )

      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tripId, user?.id]);

  const scrollToEnd = () => {
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 50); // etwas Delay, um Rendering abzuwarten
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    const { data, error } = await supabase
      .from("chat_messages")
      .insert({
        trip_id: tripId,
        user_id: user.id,
        content: newMessage.trim(),
      })
      .select();

    if (error) {
      console.error("Senden fehlgeschlagen:", error.message);
    } else {
      setNewMessage("");

      if (data && data.length > 0) {
        // ✅ eigene Nachricht lokal zur Liste hinzufügen
        setMessages((prev) => [...prev, data[0]]);
        scrollToEnd();

        // ✅ und als gelesen markieren
        await supabase.from("chat_read_status").insert({
          message_id: data[0].id,
          user_id: user.id,
          read: true,
        });
      }
    }
  };

  return (
    <div className="bg-[#0d1117] h-screen flex flex-col">
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 pt-20 pb-20 flex flex-col flex-1 min-h-0">
        {/* Titel */}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-slate-100">💬 Gruppenchat</h1>
        </div>

        {/* Nachrichtenbereich */}
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto bg-[#0d1117] rounded-xl border border-[#2a3347] p-3 mb-4 flex flex-col-reverse gap-3"
        >
          {[...messages].reverse().map((msg, index) => {
            const isOwnMessage = msg.user_id === user?.id;

            const currentDate = new Date(msg.created_at ?? "").toDateString();
            const nextDate =
              index < messages.length - 1
                ? new Date(messages[index + 1].created_at ?? "").toDateString()
                : null;

            const showDateHeader = currentDate !== nextDate;

            return (
              <div key={msg.id}>
                {showDateHeader && (
                  <div className="my-4 text-center text-sm text-slate-500">
                    <div className="inline-block border-t border-[#2a3347] w-1/5 align-middle"></div>
                    <span className="mx-3">
                      {new Date(msg.created_at ?? "").toLocaleDateString("de-CH", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    <div className="inline-block border-t border-[#2a3347] w-1/5 align-middle"></div>
                  </div>
                )}

                <div
                  className={`flex ${
                    isOwnMessage ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] md:max-w-[60%] px-4 py-2 rounded-2xl shadow
            ${
              isOwnMessage
                ? "bg-sky-500 text-white rounded-br-none"
                : "bg-[#1e2639] text-slate-200 rounded-bl-none"
            }`}
                    style={{
                      borderBottomRightRadius: isOwnMessage
                        ? "0.5rem"
                        : undefined,
                      borderBottomLeftRadius: !isOwnMessage
                        ? "0.5rem"
                        : undefined,
                    }}
                  >
                    {!isOwnMessage && (
                      <div className="text-xs font-semibold text-slate-400 mb-1">
                        {msg.profiles?.first_name} {msg.profiles?.last_name}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap text-sm">
                      {msg.content}
                    </div>
                    <div className="text-[10px] text-right text-slate-400 mt-1">
                      {new Date(msg.created_at ?? "").toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Eingabefeld */}
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 border border-[#2a3347] bg-[#161b27] text-slate-100 rounded-lg px-3 py-2 outline-none focus:border-sky-400 placeholder:text-slate-600"
            placeholder="Nachricht eingeben..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <button
            onClick={sendMessage}
            className="bg-sky-500 text-white px-4 py-2 rounded-lg hover:bg-sky-600"
          >
            Senden
          </button>
        </div>
      </div>
    </div>
  );
}
